import reactUpdate from 'immutability-helper';
import _ from 'underscore';
import {v4 as uuidv4} from 'uuid';

import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import convertAssaySchemaToDataTableConfig from 'notebook/data-tables/convertAssaySchemaToDataTableConfig';
import {DataTable, DataTableColumn, DataTableConfig} from 'notebook/data-tables/DataTableConstants';
import {createDataTable} from 'notebook/data-tables/DataTableUtil';
import {TagValueStructuredTableColumn} from 'notebook/structured-tables/StructuredTableConstants';
import {Cell} from 'notebook/tables/constants/CellConstants';
import {uniqueTableName} from 'notebook/tables/excess/Util';
import {convertTagToTypedValue} from 'notebook/tables/typed-values/DataTypes';
import {NoteLinkedObjectType} from 'notebook/util/NoteUtilConstants';
import {AssaySchema} from 'results/assay-schemas/AssaySchemaConstants';
import {AssayResult} from 'results/assays/AssayRecordConstants';
import {getTagForField} from 'results/assays/getTagForField';
import {getPrimaryLinkField, getPrimaryLinkTagForResult} from 'results/PrimaryLinks';
import {DisplayConfig} from 'runs/AttachmentConstants';
import {FIELD_TYPES, SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {SchemaFieldSelector} from 'schemas/SchemaFieldSelectorConstants';
import {Optional} from 'util/Types';

function createConfigFromPublishedColumns(
  schema: AssaySchema,
  publishedDataTableColumns: Optional<Array<DataTableColumn>>
): DataTableConfig {
  const columns = [];
  assert(publishedDataTableColumns != null, 'schema.publishedDataTableColumns are expected');
  for (const column of publishedDataTableColumns) {
    columns.push({
      ...column,
      cid: _.uniqueId('dtcol'),
    });
  }
  const primaryField = getPrimaryLinkField(schema);

  return {
    cid: _.uniqueId('dtc'),
    type: NoteLinkedObjectType.DATA_TABLE_CONFIG,
    name: schema.name,
    provenanceUuid: uuidv4(),
    isStandalone: true,
    isWatchingInputTable: false,
    watchedRegistrationProvenanceUuids: [],
    linkType: primaryField ? primaryField.fieldType : null,
    assaySchema: schema,
    columns,
  };
}

export function recreateEmptyDataTablesForAssayGroup(
  sortedSchemas: Array<AssaySchema>,
  displayConfigBySchemaId: {[schemaId: string]: DisplayConfig},
  existingDataTables: Array<DataTable>,
  existingTableNames: Array<string>,
  selectorsById: {[selectorId: string]: SchemaFieldSelector},
  publishedDataTableColumnsBySchema?: {[schemaId: string]: Optional<Array<DataTableColumn>>}
): Array<DataTable> {
  return sortedSchemas.map((schema) => {
    const existingDataTable = existingDataTables.find(
      (dataTable) => dataTable.config.assaySchema.id === schema.id
    );
    if (existingDataTable) {
      return existingDataTable;
    }
    const publishedDataTableColumns = publishedDataTableColumnsBySchema
      ? publishedDataTableColumnsBySchema[schema.id]
      : schema.publishedDataTableColumns;
    const dataTableConfig =
      publishedDataTableColumns == null
        ? convertAssaySchemaToDataTableConfig(schema, selectorsById)
        : createConfigFromPublishedColumns(schema, publishedDataTableColumns);
    const newDataTable: DataTable = createDataTable(dataTableConfig, {
      noRows: true,
    });
    const displayConfig = displayConfigBySchemaId[schema.id];

    const hiddenFieldIds = !displayConfig
      ? []
      : schema.fields
          .filter((field) => displayConfig.hiddenFields.includes(field.name))
          .map((field) => field.id);
    assert(newDataTable.rows.length === 0, 'Unsafe to remove columns if there is data');
    return reactUpdate(newDataTable, {
      config: {
        name: {
          $apply: (name: string) => {
            const newName = uniqueTableName(existingTableNames, name, {allowNoSuffix: true});
            existingTableNames.push(newName);
            return newName;
          },
        },
        columns: {
          $apply: (columns: Array<TagValueStructuredTableColumn>) =>
            columns.filter(
              (column: TagValueStructuredTableColumn) =>
                !column.tagSchemaFieldId || !hiddenFieldIds.includes(column.tagSchemaFieldId)
            ),
        },
      },
    });
  });
}

function _getSortedResults(results: Array<AssayResult>, schema: AssaySchema): Array<AssayResult> {
  // Until we support custom sorts on tables, auto-sort by <primary-link field of assay schema, createdAt>.
  // Since _.sortBy is a stable sort, we can achieve this by sorting on createdAt and then primaryLinkField
  const sortedResults = _.sortBy(results, 'createdAt');
  const primaryLinkField = getPrimaryLinkField(schema);
  if (primaryLinkField) {
    return _sortByPrimaryLinkField(sortedResults, primaryLinkField);
  }
  return sortedResults;
}

function _sortByPrimaryLinkField(
  results: Array<AssayResult>,
  primaryLinkField: SchemaField
): Array<AssayResult> {
  return _.sortBy(results, (result) => {
    const sampleTag = getPrimaryLinkTagForResult(primaryLinkField, result);
    return (sampleTag && sampleTag.value) || '';
  });
}

export function fillEmptyDataTableWithResults(
  dataTable: DataTable,
  results: Array<AssayResult>,
  schema: AssaySchema
): DataTable {
  assert(dataTable.rows.length === 0, 'Unexpectedly attempted to fill an already filled data table');
  if (!BenchlingConfig.ELN_RUN_RESULTS_SORT_BY_INSERTION) {
    results = _getSortedResults(results, schema);
  }
  const primaryField = getPrimaryLinkField(dataTable.config.assaySchema);
  return {
    ...dataTable,
    rows: results.map((result) => {
      const primaryTag = primaryField ? getTagForField(result, primaryField.id) : null;
      let dataKey;
      if (!primaryTag) {
        dataKey = {};
      } else if (primaryTag.tagSchemaField.fieldType === FIELD_TYPES.BATCH_LINK.type) {
        dataKey = {batchId: primaryTag.targetBatches![0] && primaryTag.targetBatches![0].id};
      } else if (primaryTag.tagSchemaField.fieldType === FIELD_TYPES.BIOENTITY_LINK.type) {
        dataKey = {fileId: primaryTag.targetFiles![0] && primaryTag.targetFiles![0].id};
      }
      return {
        euid: result.id,
        ...dataKey,
        cells: dataTable.config.columns.map((column) => {
          const tag = getTagForField(result, column.tagSchemaFieldId);
          const baseCell: Cell = tag
            ? {
                text: tag.value || '',
                typedValue: convertTagToTypedValue(tag),
              }
            : {text: ''};
          return {...baseCell, locked: true};
        }),
      };
    }),
  };
}
